import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Search, User, ChevronRight, Award, GraduationCap, TrendingUp, Activity } from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

const fmtMonths = (m) => {
  if (m == null) return "—";
  if (m < 12) return `${m}m`;
  const y = Math.floor(m / 12);
  const r = m % 12;
  return r === 0 ? `${y}a` : `${y}a${r}m`;
};

export default function PerformanceIndividualsPage() {
  const { companyParam } = useCompany();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (companyParam) params.company_id = companyParam;
      if (search) params.search = search;
      const { data } = await api.get("/performance/individual", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totals = useMemo(() => {
    const withScore = items.filter((i) => i.media_ultima != null);
    return {
      total: items.length,
      avaliados: withScore.length,
      media: withScore.length
        ? Math.round((withScore.reduce((acc, i) => acc + i.media_ultima, 0) / withScore.length) * 100) / 100
        : 0,
      promovidos: items.filter((i) => i.promocoes_qtd > 0).length,
    };
  }, [items]);

  return (
    <div className="space-y-5 pb-10" data-testid="perf-individuals-page">
      <PageHero
        kicker={<><Activity className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Relatórios Individuais"
        description="Selecione um colaborador para ver o dossiê completo de performance, treinamentos recomendados e análise com IA."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Colaboradores no escopo" value={totals.total} icon={User} accent="emerald" testId="pi-total" />
        <KpiCard label="Avaliados" value={totals.avaliados}
                 sub={`${totals.total - totals.avaliados} sem avaliação`} icon={Award} accent="ochre" testId="pi-evaluated" />
        <KpiCard label="Média geral" value={totals.media || "—"}
                 sub="Última avaliação" icon={TrendingUp} accent="sky" testId="pi-avg" />
        <KpiCard label="Já promovidos" value={totals.promovidos} icon={GraduationCap} accent="slate" testId="pi-promoted" />
      </div>

      <Section kicker="Roster" title="Colaboradores" testId="pi-roster">
        <div className="relative mb-3">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar por nome, matrícula ou cargo..." className="pl-9 h-10"
                 data-testid="pi-search" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[110px]" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[13px] text-muted-foreground">
            Nenhum colaborador encontrado.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((i) => (
              <Link key={i.id} to={`/performance/individuos/${i.id}`}
                    className="rounded-xl border border-border bg-white p-4 hover:shadow-md hover:border-[#234935]/30 transition-all group"
                    data-testid={`pi-card-${i.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-[#234935] text-white grid place-items-center text-[13px] font-semibold shrink-0">
                    {(i.nome || "?").charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[14px] font-semibold truncate">{i.nome}</div>
                    <div className="text-[10.5px] text-muted-foreground truncate">
                      {i.cargo || "—"} · Mat. {i.matricula}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#234935] group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-slate-50 p-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Score</div>
                    <div className="text-[13px] font-semibold tabular-nums">
                      {i.media_ultima != null ? i.media_ultima : <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Treinos</div>
                    <div className="text-[13px] font-semibold tabular-nums">{i.treinamentos_concluidos}</div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Tempo</div>
                    <div className="text-[13px] font-semibold tabular-nums">{fmtMonths(i.tempo_empresa_meses)}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0">{i.sector || "Sem setor"}</Badge>
                  {i.promocoes_qtd > 0 && (
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-800 border-amber-200">
                      {i.promocoes_qtd} promo{i.promocoes_qtd > 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
